import { create } from './StoreCreator';

import type { IStoreAction } from './StoreCreator';

import type { CheckoutStepIdType, IOrder } from 'types';

// State

interface CheckoutStoreState {
  isBillingVisible: boolean,
  isLoading: boolean,
  isDisabled: boolean,
  order: IOrder,
  notes: string,
  isValidationVisible: boolean,
  firstInvalidStep: CheckoutStepIdType
}

const initialState: CheckoutStoreState = {
  isBillingVisible: false,
  isLoading: true,
  isDisabled: false,
  order: null,
  notes: '',
  isValidationVisible: false,
  firstInvalidStep: null,
};

// Actions

const ACTIONS = {
  SET_STATE: 'SET_STATE',
};

// Reducer

const reducer = (draft: CheckoutStoreState, action: IStoreAction): CheckoutStoreState => {
  const {
    type,
    payload = {},
  } = action;

  switch (type) {

    case ACTIONS.SET_STATE:
      return {
        ...draft,
        ...payload
      };

    default:
      return draft;
  }
};

// Create

const [
  CheckoutProvider,
  useCheckoutStore,
  useCheckoutDispatch
] = create(reducer, initialState);

// Dispatch Helpers

function useCheckoutDispatchHelpers() {

  const dispatch = useCheckoutDispatch();

  // Helpers

  const updateCheckoutStatus = (order: IOrder) => {
    if (!order) {
      dispatch({
        type: ACTIONS.SET_STATE,
        payload: { isLoading: true }
      });

    } else {
      dispatch({
        type: ACTIONS.SET_STATE,
        payload: { isLoading: true, order }
      });
    }

    dispatch({
      type: ACTIONS.SET_STATE,
      payload: { isLoading: false }
    });
  };

  const setBillingVisible = (isVisible: boolean = true) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: { isBillingVisible: isVisible }
    });
  };

  const setNotes = (notes: string) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: { notes }
    });
  };

  const reset = () => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: initialState
    });
  };

  const setLoading = (loading: boolean) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: { isLoading: loading }
    });
  };

  const setDisabled = (disabled: boolean) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: { isDisabled: disabled }
    });
  };

  const setValidation = (isValidationVisible: boolean, firstInvalidStep: CheckoutStepIdType) => {
    dispatch({
      type: ACTIONS.SET_STATE,
      payload: {
        isValidationVisible,
        firstInvalidStep,
      }
    });
  };

  // Return

  return {
    updateCheckoutStatus,
    setBillingVisible,
    setNotes,
    setLoading,
    setDisabled,
    setValidation,
    reset,
  };
}

// Export

export {
  CheckoutProvider,
  useCheckoutStore,
  useCheckoutDispatch,
  useCheckoutDispatchHelpers,
  ACTIONS as CHECKOUT_ACTIONS,
};
